import { useState } from "react";
import { useSearchParams } from "react-router-dom";
import { useTranslation } from "react-i18next";
import ToolShell, { Panel, FieldLabel } from "@/components/tools/ToolShell";
import { StatusToggle, parseAmount } from "@/components/tools/ProfitTeaser";
import { TAX_CONFIG, TOOL_PATHS, calcProfitToPocket, formatEUR, formatPct } from "@/config/tools";

export default function ProfitToPocket() {
  const { t, i18n } = useTranslation();
  const lang = i18n.language;
  const [params] = useSearchParams();
  const [profit, setProfit] = useState(parseAmount(params.get("profit")) || TAX_CONFIG.defaultProfit);
  const [status, setStatus] = useState(params.get("status") === "domiciled" ? "domiciled" : "non_dom");
  const [distribution, setDistribution] = useState(100);
  const r = calcProfitToPocket({ profit, status, distribution });
  const assumptions = t("tools.profit.assumptions", { returnObjects: true });

  const rows = [
    ["profit", r.profit],
    ["cit", -r.cit, formatPct(TAX_CONFIG.corporateTax, lang)],
    ["afterCit", r.afterCit, null, true],
    ...(r.retained > 0 ? [["retained", -r.retained]] : []),
    ["dividend", r.dividend],
    ["sdc", -r.sdc, formatPct(r.sdcRate, lang)],
    ["gesy", -r.gesy, formatPct(TAX_CONFIG.gesyDividend, lang, 2)],
  ];

  return (
    <ToolShell id="profit" path={TOOL_PATHS.profit}>
      <div className="grid gap-px bg-[var(--line)] lg:grid-cols-12">
        <Panel className="lg:col-span-5 bg-[var(--ink)] border-0 space-y-10">
          <div>
            <FieldLabel htmlFor="p2p-profit">{t("tools.teaser.profit")}</FieldLabel>
            <input
              id="p2p-profit"
              inputMode="numeric"
              dir="ltr"
              className="field !text-3xl"
              value={profit ? new Intl.NumberFormat("en-US").format(profit) : ""}
              onChange={(e) => setProfit(parseAmount(e.target.value))}
              data-testid="p2p-profit-input"
            />
          </div>
          <div>
            <FieldLabel>{t("tools.teaser.status")}</FieldLabel>
            <StatusToggle value={status} onChange={setStatus} testid="p2p-status" />
          </div>
          <div>
            <FieldLabel htmlFor="p2p-dist">
              {t("tools.profit.distribution")} · <span className="text-[var(--bone)]">{distribution}%</span>
            </FieldLabel>
            <input
              id="p2p-dist"
              type="range"
              min="0"
              max="100"
              step="5"
              value={distribution}
              onChange={(e) => setDistribution(Number(e.target.value))}
              className="w-full accent-[var(--pewter)]"
              data-testid="p2p-distribution"
            />
          </div>
        </Panel>
        <Panel className="lg:col-span-7 bg-[var(--ink-2)] border-0" data-testid="p2p-breakdown">
          <dl className="divide-y divide-[var(--line)]">
            {rows.map(([key, val, rate, strong]) => (
              <div key={key} className="flex items-baseline justify-between gap-6 py-4" data-testid={`p2p-row-${key}`}>
                <dt className={strong ? "text-[var(--bone)]" : "text-[var(--bone-2)]"}>
                  {t(`tools.profit.rows.${key}`)}
                  {rate && <span className="ms-2 font-mono text-[10px] tracking-[0.16em] text-[var(--mute)]">{rate}</span>}
                </dt>
                <dd dir="ltr" className={`font-mono text-sm whitespace-nowrap ${val < 0 ? "text-[var(--mute)]" : "text-[var(--bone)]"}`}>
                  {val < 0 ? "− " : ""}{formatEUR(Math.abs(val), lang)}
                </dd>
              </div>
            ))}
          </dl>
          <div className="mt-6 pt-8 border-t border-[var(--line-2)]">
            <div className="font-mono text-[10px] uppercase tracking-[0.2em] text-[var(--pewter)]">{t("tools.profit.rows.pocket")}</div>
            <div className="display text-[var(--bone)] mt-2" style={{ fontSize: "clamp(44px, 6vw, 80px)", lineHeight: 1 }} data-testid="p2p-pocket">
              {formatEUR(r.pocket, lang)}
            </div>
            <div className="mt-4 flex flex-wrap gap-x-8 gap-y-2 text-sm text-[var(--mute)]">
              <span>{t("tools.profit.rows.totalTax")}: <span className="text-[var(--bone-2)]" data-testid="p2p-total-tax">{formatEUR(r.totalTax, lang)}</span></span>
              <span>{t("tools.profit.rows.effective")}: <span className="text-[var(--bone-2)]" data-testid="p2p-effective">{formatPct(r.effectiveRate, lang)}</span></span>
            </div>
          </div>
        </Panel>
      </div>
      <div className="mt-10 max-w-3xl">
        <div className="overline mb-4">{t("tools.profit.assumptionsTitle")}</div>
        <ul className="space-y-2 text-sm text-[var(--mute)] list-disc ps-5">
          {assumptions.map((a, i) => <li key={i}>{a}</li>)}
        </ul>
      </div>
    </ToolShell>
  );
}
